"use client";

import { c as _c } from "react/compiler-runtime";
import { useDocumentActionsContext } from "@hooks/pages/use-document-actions";
import { IconWritingSign } from "@tabler/icons-react";
import React, { useEffect, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ContainerWithHeaderSimple } from "@components/common/containers/container-with-header";
import { ExternalLink } from "@components/common/external-link";
import { Button } from "@components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@components/ui/card";
import { Skeleton } from "@components/ui/skeleton";
import { environment } from "@lib/environment";
import { ProposalsCountByMonthStatsWidget } from "@features/events-program/ui/widgets/proposals-count-by-month-stats-widget";
import { useGetSpeakersProposalsQuery } from "@features/proposals/proposals-api";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { ProposalCard } from "@features/proposals/ui/proposal-card";
export const ProposalsListController = () => {
  const $ = _c(48);
  const t = useTranslations("features.proposals.ui.proposalsListController");
  const {
    setDocumentHeaderNode
  } = useDocumentActionsContext().handlers;
  const locale = useLocale();
  const {
    data: proposals,
    isLoading: proposalsLoading
  } = useGetSpeakersProposalsQuery(undefined);
  let t0;
  if ($[0] !== proposals) {
    const active = [];
    const closed = [];
    proposals?.forEach(p => {
      if (proposalsTools.filters.isNonClosedProposal(p)) {
        active.push(p);
      } else {
        if (proposalsTools.filters.isClosedProposal(p)) {
          closed.push(p);
        }
      }
    });
    t0 = {
      activeProposals: active,
      closedProposals: closed
    };
    $[0] = proposals;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    activeProposals,
    closedProposals
  } = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-t-xl opacity-50 [background:repeating-linear-gradient(135deg,rgba(0,0,0,0.06)_0_1px,transparent_2px_14px)] dark:opacity-25 dark:[background:repeating-linear-gradient(135deg,rgba(255,255,255,0.07)_0_1px,transparent_2px_14px)]" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("needMoreProposals");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div className="relative -mt-6 flex min-h-32 w-full flex-col items-center justify-center overflow-hidden rounded-t-xl bg-muted/50 px-4 text-center text-xl font-medium dark:bg-muted/80">{t1}{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== locale) {
    t4 = environment.urls.external.getCfpUrl(locale);
    $[7] = locale;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("submitProposal");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <ExternalLink href={t4} className="no-underline hover:underline">{t5}</ExternalLink>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconWritingSign size={70} stroke={1} className="text-foreground/90" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6) {
    t8 = <CardFooter className="flex h-full flex-col items-center justify-evenly text-sm">{t6}{t7}</CardFooter>;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t3 || $[18] !== t8) {
    t9 = <Card key={0} className="shadow-xs">{t3}{t8}</Card>;
    $[17] = t3;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const NeedMoreProposalsCard = t9;
  let t10;
  if ($[20] !== activeProposals?.length || $[21] !== closedProposals?.length || $[22] !== locale || $[23] !== t) {
    t10 = () => <Card className="@container/card shadow-xs"><CardHeader><CardDescription>{t("header.opened")}</CardDescription><CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">{activeProposals?.length === undefined && <Skeleton className="h-9 w-5 rounded-md bg-gray-200" />}{activeProposals?.length}</CardTitle><CardDescription>{t("header.closed")}</CardDescription><CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">{activeProposals?.length === undefined && <Skeleton className="h-9 w-5 rounded-md bg-gray-200" />}{closedProposals?.length}</CardTitle></CardHeader><CardFooter className="flex h-full flex-col items-start gap-2 text-sm">{activeProposals?.length === 0 && <div className="line-clamp-1 flex gap-2 font-medium">{t("noOpenedProposals")}</div>}<div className="mt-auto text-muted-foreground"><ExternalLink href={environment.urls.external.getCfpUrl(locale)} className="no-underline hover:underline">{t("submitProposal")}</ExternalLink></div></CardFooter></Card>;
    $[20] = activeProposals?.length;
    $[21] = closedProposals?.length;
    $[22] = locale;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  activeProposals?.length;
  closedProposals?.length;
  let t11;
  if ($[25] !== t10) {
    t11 = t10();
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const ProposalsStatCard = t11;
  let t12;
  let t13;
  if ($[27] !== setDocumentHeaderNode || $[28] !== t) {
    t12 = () => {
      setDocumentHeaderNode({
        node: <Button variant="link">{t("header.dontShowedProposals")}</Button>
      });
    };
    t13 = [setDocumentHeaderNode, t];
    $[27] = setDocumentHeaderNode;
    $[28] = t;
    $[29] = t12;
    $[30] = t13;
  } else {
    t12 = $[29];
    t13 = $[30];
  }
  useEffect(t12, t13);
  let t14;
  if ($[31] !== proposals || $[32] !== proposalsLoading) {
    t14 = <ProposalsCountByMonthStatsWidget proposals={proposals} isLoading={proposalsLoading} />;
    $[31] = proposals;
    $[32] = proposalsLoading;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== ProposalsStatCard || $[35] !== t14) {
    t15 = <div className="grid auto-rows-min gap-4 md:grid-cols-2 xl:grid-cols-3">{ProposalsStatCard}{t14}</div>;
    $[34] = ProposalsStatCard;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== NeedMoreProposalsCard || $[38] !== activeProposals || $[39] !== t) {
    t16 = activeProposals && activeProposals?.length > 0 && <ContainerWithHeaderSimple headerLabel={t("header.opened")}><div className="grid auto-rows-min gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">{activeProposals?.map(_temp)}{NeedMoreProposalsCard}</div></ContainerWithHeaderSimple>;
    $[37] = NeedMoreProposalsCard;
    $[38] = activeProposals;
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== closedProposals || $[42] !== t) {
    t17 = closedProposals && closedProposals?.length > 0 && <ContainerWithHeaderSimple headerLabel={t("header.closed")}><div className="grid auto-rows-min gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">{closedProposals?.map(_temp2)}</div></ContainerWithHeaderSimple>;
    $[41] = closedProposals;
    $[42] = t;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t15 || $[45] !== t16 || $[46] !== t17) {
    t18 = <div className="flex h-full flex-col gap-6">{t15}{t16}{t17}</div>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};
function _temp(proposal) {
  return <ProposalCard key={proposal.id} className="" proposal={proposal} />;
}
function _temp2(proposal_0) {
  return <ProposalCard key={proposal_0.id} proposal={proposal_0} />;
}